import { type CSSResultGroup, LitElement, unsafeCSS, html, nothing } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { emit, createFocusTrapSentinels } from '@/navbar/utils'
import { $t, localized } from '@/navbar/i18n'
import type { MenuItem, EcoAssist } from '@/navbar/navbar.types'

// Styles:
import styles from './eco-suite-navbar-mobile-menu.scss?inline'

/**
 * Slide-in mobile navigation panel used internally by `<eco-suite-navbar>`.
 *
 * Focus management: opening the panel does not move focus (the trigger
 * retains focus per ARIA APG disclosure guidance). While open, focus is
 * trapped inside the panel via a pair of invisible focus sentinels at the
 * start and end of the shadow tree, so keyboard users who Tab into the panel
 * cycle within it.
 *
 * @internal
 * @event eco-mobile-menu-opened - Fired when the panel begins to open.
 * @event eco-mobile-menu-closed - Fired when the panel begins to close.
 */
@localized()
@customElement('eco-suite-navbar-mobile-menu')
export class EcoSuiteNavbarMobileMenu extends LitElement {
  static readonly styles: CSSResultGroup = [unsafeCSS(styles)]
  private _closeWatcher?: CloseWatcher | null

  private readonly _sentinelHandlers = createFocusTrapSentinels(
    () => this.focusFirstElement(),
    () => this.focusLastElement(),
  )

  private readonly handleSentinelStartFocus = this._sentinelHandlers.start
  private readonly handleSentinelEndFocus = this._sentinelHandlers.end

  @query('.EcoSuiteNavbarMobileMenu')
  private readonly menuElement!: HTMLElement

  @property({ attribute: 'user-name' })
  userName?: string

  @property({ attribute: false })
  ecoAssist?: EcoAssist

  @property({ attribute: false })
  disableAssist?: boolean

  @property({ attribute: false })
  accountMenuItems?: MenuItem[]

  @property({ attribute: false })
  onLogout?: (e: Event) => void

  @property()
  logoutHref?: string

  @property({ attribute: false })
  helpMenuItems?: MenuItem[]

  @property()
  helpHref?: string

  @property({ attribute: false })
  onHelpClick?: (e: Event) => void

  @property({ type: Boolean })
  isGuest = false

  @property({ type: Boolean })
  hideAccountMenu = false

  @state()
  private isOpen = false

  disconnectedCallback(): void {
    this.removeOpenListeners()
    super.disconnectedCallback()
  }

  public toggle(): void {
    if (this.isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  public open(): void {
    if (this.isOpen) return
    this.isOpen = true
    this.addOpenListeners()
    emit(this, 'eco-mobile-menu-opened', true)
  }

  public close(): void {
    if (!this.isOpen) return
    this.removeOpenListeners()
    this.isOpen = false
    emit(this, 'eco-mobile-menu-closed', true)
  }

  private readonly handleDocumentMouseDown = (event: MouseEvent) => {
    const path = event.composedPath()
    if (!path.includes(this)) this.close()
  }

  private readonly handleDocumentKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      this.close()
    }
  }

  /**
   * Returns focusable elements inside the panel, including both shadow-DOM
   * descendants and slotted light-DOM descendants. The trap only needs the
   * first and last entries (boundaries) — native Tab order handles everything
   * in between, including focusables nested inside child custom elements'
   * shadow roots.
   */
  private getFocusableElements(): HTMLElement[] {
    if (!this.menuElement) return []

    const accountMenu = this.menuElement.querySelector('eco-suite-navbar-account-menu')!
    const helpMenu = this.menuElement.querySelector('eco-suite-navbar-help')! as HTMLElement
    const assistButton = this.menuElement.querySelector('eco-assist-trigger-icon')! as HTMLElement

    // Slotted (light-DOM) descendants of the host aren't reachable via the
    // shadow querySelectorAll above; collect them via assignedElements and
    // include their own descendants.
    const selector = [
      'eco-suite-navbar-button',
      'eco-suite-navbar-menu',
    ].join(',')
    const utilitiesSlot = this.menuElement.querySelector<HTMLSlotElement>('slot')
    const lightMatches: HTMLElement[] = []
    if (utilitiesSlot) {
      const assigned = utilitiesSlot.assignedElements({ flatten: true }) as HTMLElement[]
      for (const node of assigned) {
        if (node.matches?.(selector)) lightMatches.push(node)
        lightMatches.push(...Array.from(node.querySelectorAll<HTMLElement>(selector)))
      }
    }

    return [accountMenu, ...lightMatches, helpMenu, assistButton]
  }

  private focusFirstElement(): void {
    const focusables = this.getFocusableElements()
    focusables[0]?.focus()
  }

  private focusLastElement(): void {
    const focusables = this.getFocusableElements()
    focusables[focusables.length - 1]?.focus()
  }

  private addOpenListeners() {
    this.removeOpenListeners()
    if ('CloseWatcher' in globalThis) {
      this._closeWatcher = new CloseWatcher()
      this._closeWatcher.onclose = () => {
        this.close()
      }
    }
    document.addEventListener('mousedown', this.handleDocumentMouseDown)
    document.addEventListener('keydown', this.handleDocumentKeyDown)
  }

  private removeOpenListeners() {
    document.removeEventListener('mousedown', this.handleDocumentMouseDown)
    document.removeEventListener('keydown', this.handleDocumentKeyDown)
    this._closeWatcher?.destroy()
    this._closeWatcher = null
  }

  render() {
    return html`
      <nav
        class=${classMap({
          EcoSuiteNavbarMobileMenu: true,
          'EcoSuiteNavbarMobileMenu--open': this.isOpen,
        })}
        aria-hidden=${this.isOpen ? 'false' : 'true'}
        aria-label=${$t('navbar.menu')}
      >
        <span
          class="EcoSuiteNavbarMobileMenu__sentinelStart"
          tabindex=${this.isOpen ? '0' : '-1'}
          aria-hidden="true"
          @focus=${this.handleSentinelStartFocus}
        ></span>

        ${
          this.hideAccountMenu ? nothing : html`
            <eco-suite-navbar-account-menu
              .isGuest=${this.isGuest}
              .userName=${this.userName}
              .menuItems=${this.accountMenuItems}
              .onLogout=${this.onLogout}
              .logoutHref=${this.logoutHref}
            >
              <slot name="account-menu"></slot>
            </eco-suite-navbar-account-menu>
          `
        }

        <slot name="utilities"></slot>
        <eco-suite-navbar-help
          .menuItems=${this.helpMenuItems}
          .href=${this.helpHref}
          .onHelpClick=${this.onHelpClick}
        >
          <slot name="help-menu"></slot>
        </eco-suite-navbar-help>
        ${
          (this.ecoAssist && !this.disableAssist)
            ? html`
              <eco-assist-trigger-icon
                .ecoAssist=${this.ecoAssist}
                data-qa-id="assist-button"
              ></eco-assist-trigger-icon>
            `
            : ''
        }
        <span
          class="EcoSuiteNavbarMobileMenu__sentinelEnd"
          tabindex=${this.isOpen ? '0' : '-1'}
          aria-hidden="true"
          @focus=${this.handleSentinelEndFocus}
        ></span>
      </nav>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'eco-suite-navbar-mobile-menu': EcoSuiteNavbarMobileMenu
  }
}
